import { useEffect, useState } from 'react';
import { Button, Input, Modal, Select } from '@react-apps/design-system';
import type { TotvsSyncStatus } from '../../domain/entities/totvs.entity';
import { TOTVS_SYNC_STATUSES } from '../../domain/entities/totvs.entity';
import { getTotvsStatusLabel } from '../display';
import { ContactSearchInput } from '@/shared/contact-search/ContactSearchInput';

export interface TotvsSyncTasksFilters {
  status: TotvsSyncStatus | '';
  productExternalId: string;
  errorCode: string;
  contactUserId: string;
}

export const EMPTY_TOTVS_SYNC_FILTERS: TotvsSyncTasksFilters = {
  status: '',
  productExternalId: '',
  errorCode: '',
  contactUserId: '',
};

export function countActiveTotvsSyncFilters(
  filters: TotvsSyncTasksFilters,
): number {
  return (
    (filters.status ? 1 : 0) +
    (filters.productExternalId.trim() ? 1 : 0) +
    (filters.errorCode.trim() ? 1 : 0) +
    (filters.contactUserId ? 1 : 0)
  );
}

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  ...TOTVS_SYNC_STATUSES.map((status) => ({
    value: status,
    label: getTotvsStatusLabel(status),
  })),
];

interface TotvsSyncTasksFiltersModalProps {
  open: boolean;
  onClose: () => void;
  filters: TotvsSyncTasksFilters;
  onApply: (filters: TotvsSyncTasksFilters) => void;
}

export function TotvsSyncTasksFiltersModal({
  open,
  onClose,
  filters,
  onApply,
}: TotvsSyncTasksFiltersModalProps) {
  const [draft, setDraft] = useState(filters);
  const [contactTerm, setContactTerm] = useState('');

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const update = <K extends keyof TotvsSyncTasksFilters>(
    key: K,
    value: TotvsSyncTasksFilters[K],
  ) => setDraft((prev) => ({ ...prev, [key]: value }));

  const handleApply = () => {
    onApply({
      status: draft.status,
      productExternalId: draft.productExternalId.trim(),
      errorCode: draft.errorCode.trim(),
      contactUserId: draft.contactUserId,
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Filtrar jobs TOTVS"
      description="Filtra a fila de sincronização direto no servidor. O mesmo status/código de erro selecionado aqui pode ser reprocessado em lote na listagem."
      size="lg"
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setDraft(EMPTY_TOTVS_SYNC_FILTERS);
              setContactTerm('');
            }}
          >
            Limpar
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleApply}>
            Aplicar filtros
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <ContactSearchInput
          value={draft.contactUserId || null}
          onChange={(userId) =>
            setDraft((prev) => ({ ...prev, contactUserId: userId ?? '' }))
          }
          term={contactTerm}
          onTermChange={setContactTerm}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Status"
            id="totvs-filter-status"
            options={STATUS_OPTIONS}
            value={draft.status}
            onChange={(e) =>
              update('status', e.target.value as TotvsSyncStatus | '')
            }
          />
          <Input
            label="Produto externo"
            placeholder="ID do produto no gateway"
            value={draft.productExternalId}
            onChange={(e) => update('productExternalId', e.target.value)}
          />
          <Input
            label="Código de erro"
            placeholder="Ex.: TRANSITORIO, CONFIG…"
            value={draft.errorCode}
            onChange={(e) => update('errorCode', e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
